import { useRef, useMemo } from 'react';

import TextField from '@mui/material/TextField';
import { Autocomplete, createFilterOptions } from '@mui/material';

import { useGetOrgBrands } from 'src/actions/org';
import { useGetMyBrands } from 'src/actions/user';

// ----------------------------------------------------------------------

const filterOptions = createFilterOptions({ sort: false });

const sortByBrandOrder = (brands) =>
  [...brands].sort((a, b) => {
    const orderA = a.brandOrder ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.brandOrder ?? Number.MAX_SAFE_INTEGER;
    return orderA - orderB;
  });

export function BrandSelect({
  name,
  native,
  children,
  slotProps,
  helperText,
  inputProps,
  InputLabelProps,
  multiple,
  filterByUser,
  ...other
}) {
  const userBrands = useGetMyBrands({ params: { size: 'all' } });
  const orgBrands = useGetOrgBrands({ params: { size: 'all' } });
  const { data: { results: brands } } = filterByUser ? userBrands : orgBrands;

  const sortedBrands = useMemo(() => sortByBrandOrder(brands || []), [brands]);

  const hasCalled = useRef(false);
  if (!hasCalled.current && other?.onDataLoaded && sortedBrands.length) {
    hasCalled.current = true;
    other.onDataLoaded(sortedBrands);
  }

  const dict = useMemo(() => {
    return sortedBrands.reduce((acc, brand) => {
      acc[brand.brandId] = brand;
      return acc;
    }, {});
  }, [sortedBrands]);

  const renderInput = (params) => {
    const baseField = {
      ...params,
      label: 'Brand',
      placeholder: 'Select Brand',
      helperText,
      hiddenLabel: true,
      error: false,
      inputProps: {
        ...params.inputProps,
        autoComplete: 'off',
      },
    };

    return <TextField {...baseField} />;
  }

  const value = useMemo(() => {
    if (Object.keys(dict).length === 0) {
      return Array.isArray(other.value) ? [] : null;
    }

    if (Array.isArray(other.value)) {
      return other.value.map((id) => dict[id]);
    }
    return dict[other.value] || null;
  }, [other.value, dict]);

  return (
    <Autocomplete
      id={`brand-select-${name}`}
      options={sortedBrands}
      multiple={multiple}
      renderInput={renderInput}
      filterOptions={filterOptions}
      getOptionLabel={(option) => option.name ?? ''}
      getOptionKey={(option) => option.brandId}
      isOptionEqualToValue={(option, value) => option.brandId === value.brandId}
      {...other}
      onChange={(event, newValue, reason, details) => {
        if (other?.onChange) {
          other.onChange(event, newValue, reason, details);
        }
      }}
      value={value}
    />
  );
}
